<template>
  <Container
    :class="getItemTableContainerClass"
  >
    <UserGroupItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :details="details"
      :detailsButtonType="detailsButtonType"
      :detailsComponent="detailsComponent"
      :detailsIconLeft="detailsIconLeft"
      :inDetailsAsidePanel="inDetailsAsidePanel"
      :panelType="panelType"
      :showType="showType"
      :watchSelectedItems="watchSelectedItems"
      @selectedItemsChanged="handleSelectedItemsChanged"
    />
    <NotificationInPage
      :class="$style.notification"
      data-test-id="NotificationInPage__automationWarning"
      :notification="userAsidePanelNotificationConfig"
      :showNotification="showWarning"
      @close="handleNotificationClose"
    >
      <template
        v-slot:message
      >
        <div :class="$style.notificationMessage">
          <span>{{ notificationSettings.firstBodyPart }}
            <strong>{{ notificationSettings.labelBodyPart }}</strong>
            {{ notificationSettings.secondBodyPart }}</span>
          <Link
            data-test-id="NotificationInPage__addExemptionLink"
            href="#"
            :text="notificationSettings.text"
            @click.native="handleLinkClicked"
          />
        </div>
      </template>
    </NotificationInPage>
  </Container>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  Container, Link, NotificationInPage, Types,
} from '@jumpcloud/ui-components';
import { mapActions, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { some } from 'lodash';
import AsidePanelMethodMixin from 'client/components/AsidePanel/mixins/AsidePanelMethodMixin';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import UserGroupItemTable from 'client/components/ItemTable/UserGroupItemTable.vue';

const {
  ItemTableConsts,
  ItemTypeConsts,
  StoreModuleMap,
} = Constants;

const {
  userGroup,
} = ItemTypeConsts;

const { NotificationConfig } = Types;

const systemUserModel = 'SystemUserModel';
const userGroupCollection = StoreModuleMap[userGroup];

export default {
  name: 'SystemUserUserGroups',

  components: {
    Container,
    Link,
    NotificationInPage,
    UserGroupItemTable,
  },

  mixins: [
    AsidePanelMethodMixin,
    AsidePanelPropsMixin,
    ItemTableComputedMixin,
    ItemTableMethodsMixin,
  ],

  props: {
    boundItemsUrl: {
      type: String,
      required: true,
    },
    details: {
      default: false,
      type: Boolean,
    },
    detailsButtonType: {
      default: ItemTableConsts.detailsButtonType.default,
      type: String,
    },
    detailsComponent: {
      default: '',
      type: [String, Object],
    },
    detailsIconLeft: {
      type: Boolean,
      default: false,
    },
    inDetailsAsidePanel: {
      type: Boolean,
      required: true,
    },
    showType: {
      default: true,
      type: Boolean,
    },
    watchSelectedItems: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const notificationSettings = {
      firstBodyPart: 'Warning: This group has attribute value conditions that do not match this user\'s attributes. Would you like to add',
      labelBodyPart: '',
      secondBodyPart: 'to the group\'s exemption list to prevent a membership override?',
      text: 'Add User To Exemption List',
    };

    return {
      notificationSettings,
      panelType: ItemTableConsts.panelType.userAsidePanel,
      showWarning: false,
      userGroupsList: {
        userGroups: [],
        action: '',
      },
    };
  },

  computed: {
    ...mapState(systemUserModel, [
      'item',
    ]),

    getFullName() {
      return `${this.item.firstname ?? ''} ${this.item.lastname ?? ''}`;
    },

    userAsidePanelNotificationConfig() {
      return NotificationConfig({
        icon: 'error',
        title: 'Membership Condition Conflict',
        type: 'warning',
        isMarkdown: false,
        showCloseButton: true,
      });
    },
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions(userGroupCollection, {
      fetchDecoratedUserGroups: 'fetchDecoratedUserGroups',
      selectItems: 'selectItems',
      unselectItems: 'unselectItems',
      updateItem: 'updateItem',
    }),

    async handleLinkClicked() {
      const user = {
        id: this.item._id,
        type: 'USER',
      };

      const { userGroups } = this.userGroupsList;

      const userGroupsNames = userGroups.map((item) => item.name).join(',');

      const successMessage = `${this.getFullName} was added to ${userGroupsNames} Exemption List`;

      this.showWarning = false;

      await Promise.all(userGroups.map(async (group) => {
        const { id, memberQueryExemptions } = group;

        this.updateItem({
          id,
          attributes: {
            ...group,
            memberQueryExemptions: [...memberQueryExemptions, user],
          },
        });
      })).then(() => {
        this.triggerSuccessNotification({
          icon: 'checkmarkCircle',
          title: successMessage,
        });
      }).catch(() => this.triggerErrorNotification({ title: 'Unable to add user to exemption list.' }));
    },

    handleNotificationClose() {
      this.showWarning = !this.showWarning;
    },

    async handleSelectedItemsChanged(userGroupsIds, action) {
      if (userGroupsIds && userGroupsIds.length) {
        const userGroups = await this.fetchDecoratedUserGroups({ ids: userGroupsIds });

        const { _id } = this.item;

        const user = {
          type: 'USER',
          id: _id,
        };

        const automatedUserGroupsWithoutExemptions = userGroups
          .filter((ug) => ug.membershipAutomated
        && !some(ug.memberQueryExemptions, user));

        const automatedUserGroupsWithExemptions = userGroups
          .filter((ug) => ug.membershipAutomated
        && some(ug.memberQueryExemptions, user));

        const actionValidation = action === 'selected'
          ? (automatedUserGroupsWithoutExemptions.length)
          : (automatedUserGroupsWithoutExemptions.length
          && !automatedUserGroupsWithExemptions.length && !this.showWarning);

        this.notificationSettings.labelBodyPart = this.getFullName;
        this.userGroupsList.userGroups = automatedUserGroupsWithoutExemptions;
        this.userGroupsList.action = action;
        this.showWarning = !!actionValidation;
      } else {
        this.showWarning = false;
      }
    },
  },

};
</script>

<style module>
:global([data-test-id="NotificationInPage__addExemptionLink"]) {
  margin-top: 1.063rem;
}

:global([data-test-id="NotificationInPage__automationWarning"]) {
  margin: 0 auto 1rem;

  /* need to force override on this because there are issues with other components */
  max-width: calc(52% - 25rem) !important;
  position: absolute;
  right: 2.125rem;
  top: 0.188rem;
  width: 100%;
  z-index: 1001;
}

.notification,
.notificationMessage {
  display: flex;
  flex-direction: column;
}
</style>
